import theme from '@/style';
import React, {useMemo, useEffect, useState} from 'react';
import {View} from 'react-native';
import {useInnerStyle} from './vip.hooks';
import VipCardList from './vip-card-list';
import VipTableList from './vip-table-list';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {debounce, goBack, goTo} from '@/utils';
import {
  VipProgressInfo,
  getVipRender,
  maxVipLevel,
} from '@/components/business/vip';
import RechargeButton from '@/components/business/recharge-button';
import {
  IVipConfigItem,
  IVipItem,
  postVipConfig,
  postVipInfo,
} from '@/services/global.service';
import globalStore from '@/services/global.state';
import {useTranslation} from 'react-i18next';

const Vip = () => {
  const {i18n} = useTranslation();
  const {vipStyle} = useInnerStyle();
  const [vipList, setVipList] = useState<IVipItem[]>([]);
  const [vipConfigList, setVipConfigList] = useState<IVipConfigItem[]>([]);
  const [checkIndex, setCheckIndex] = useState(0);
  const cards = useMemo(() => {
    const vips = vipList.map(v => v.level);
    return vips.map(lv => getVipRender(lv));
  }, [vipList]);
  const currentLevel = useMemo(() => {
    let lv = 0;
    for (let i = 0; i < vipList.length; i++) {
      const vip = vipList[i];
      lv = vip.level;
      if (vip.statusReached === 0) {
        return Math.min(lv, maxVipLevel);
      }
    }
    return maxVipLevel;
  }, [vipList]);
  const vipInfoList = useMemo<VipProgressInfo[]>(() => {
    return vipList.map(vip => ({
      currentBadge: cards[vip.level].badge,
      nextBadge: cards[Math.min(vip.level + 1, maxVipLevel)].badge,
      current: vip.level <= currentLevel ? vip.amount - vip.diff : 0,
      total: vip.amount,
    }));
  }, [vipList, cards, currentLevel]);
  const rechargeAmount = useMemo(() => {
    if (vipList.length === 0) {
      return 0;
    }
    return (
      vipList[vipList.length - 1].amount - vipList[vipList.length - 1].diff
    );
  }, [vipList]);

  const handleRefresh = () => {
    globalStore.globalLoading.next(true);
    Promise.allSettled([postVipInfo(), postVipConfig()])
      .then(([_listvalue, _config]) => {
        if (_listvalue.status === 'fulfilled') {
          const _list = _listvalue.value;
          setVipList(
            _list.length > maxVipLevel
              ? _list.slice(0, maxVipLevel + 1)
              : _list,
          );
        }
        if (_config.status === 'fulfilled') {
          setVipConfigList(_config.value);
        }
      })
      .finally(() => globalStore.globalLoading.next(false));
  };
  useEffect(() => {
    handleRefresh();
  }, []);

  const handleChangeCheckIndex = debounce((index: number) => {
    setCheckIndex(index);
  });
  const toRecharge = () => {
    if (!globalStore.token) {
      goTo('Login');
      return;
    }
    goTo('Recharge');
  };
  return (
    <View
      style={[
        theme.fill.fillW,
        theme.flex.col,
        vipStyle.container,
        theme.background.lightGrey,
      ]}>
      <DetailNavTitle
        title={i18n.t('vip.title')}
        onBack={() => goBack()}
        hideServer
      />
      <VipCardList
        vipInfoList={vipInfoList}
        cards={cards}
        vipList={vipList}
        rechargeAmount={rechargeAmount}
        onRecharge={toRecharge}
        onCheck={handleChangeCheckIndex}
        checkIndex={checkIndex}
        currentLevel={currentLevel}
        onRefresh={handleRefresh}
      />
      <VipTableList
        cards={cards}
        vipList={vipList}
        onCheck={handleChangeCheckIndex}
        checkIndex={checkIndex}
        vipConfigList={vipConfigList}
        currentLevel={currentLevel}
      />
      <RechargeButton onRecharge={toRecharge} />
    </View>
  );
};

export default Vip;
